import { useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useGetBuyerInventories } from '~/api';
import {
  statusFromWeeksCover,
  weeksCoverForInventory,
} from '~/utils/inventory';
import { ProductStockColumn } from './shared/ProductStockColumn';

export function InventoryPrioritiesColumn() {
  const { t } = useTranslation('inventory');
  const { t: td } = useTranslation('dashboard');
  // Include inventory-only (cut-to) lengths: a buyer can't reorder them, but still wants to see
  // them when they run low on the dashboard's low-stock awareness widget.
  const { data, isLoading, isError } = useGetBuyerInventories(0, 100, {
    includeInventoryLengths: true,
  });

  const rows = useMemo(() => {
    if (!data?.buyerInventory?.content) return [];

    return [...data.buyerInventory.content]
      .filter((inv): inv is NonNullable<typeof inv> => {
        if (!inv) return false;
        const cfg = inv.buyerInventoryConfiguration;
        if (!cfg) return false;
        const weeks = weeksCoverForInventory(inv);
        return (
          weeks <= cfg.weeksCoveredBeforeUrgent ||
          weeks <= cfg.weeksCoveredBeforeMonitor
        );
      })
      .sort((a, b) => {
        const aWeeks = weeksCoverForInventory(a);
        const bWeeks = weeksCoverForInventory(b);

        if (!isFinite(aWeeks) && !isFinite(bWeeks)) return 0;
        if (!isFinite(aWeeks)) return 1;
        if (!isFinite(bWeeks)) return -1;
        return aWeeks - bWeeks;
      })
      .slice(0, 5)
      .map((inv) => {
        const cfg = inv.buyerInventoryConfiguration!;
        const weeks = weeksCoverForInventory(inv);
        const city = inv.location.facility?.address?.city ?? '';
        const productLink = `/order-builder/view?platformProductId=${inv.platformProductsLength.platformProduct.id}&returnPath=/dashboard`;

        return {
          id: inv.id,
          productName:
            inv.platformProductsLength.platformProduct.name +
            (inv.platformProductsLength.lengthDisplayValue
              ? ` ${inv.platformProductsLength.lengthDisplayValue}`
              : ''),
          productLink,
          label: statusFromWeeksCover(
            weeks,
            cfg.weeksCoveredBeforeUrgent,
            cfg.weeksCoveredBeforeMonitor,
            t
          ),
          metaItems: [
            city,
            `${isFinite(weeks) ? weeks.toFixed(1) : '∞'} wks`,
            `${cfg.usageRatePerWeek.value.toFixed(1)} ${cfg.usageRatePerWeek.unit}/wk`,
          ].filter(Boolean),
          action: (
            <Link
              to={productLink}
              className="text-xs font-medium text-prim hover:text-prim-hover"
            >
              {td('dashboard.inventory_priorities.compare_link')}
            </Link>
          ),
        };
      });
  }, [data, t, td]);

  return (
    <ProductStockColumn
      title={td('dashboard.inventory_priorities.title')}
      subtitle={td('dashboard.inventory_priorities.subtitle')}
      footerLink="/buyer-inventory"
      footerLabel={td('dashboard.inventory_priorities.footer')}
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      emptyText={td('dashboard.inventory_priorities.empty')}
      errorText={td('dashboard.inventory_priorities.error')}
    />
  );
}
